/* eslint-disable camelcase */
import { gtag } from "#js/components/utils"
import insightsClient from "search-insights"
import { getUrlWithTracking } from "#js/components/utm"

insightsClient("init", {
  appId: globalThis.algoliaAppId,
  apiKey: globalThis.algoliaAPIKey,
  authenticatedUserToken: globalThis.userId,
})

/** The structure used for the item object in the tracking events. */
export class AnalyticsItem {
  item_id
  item_name
  item_brand
  item_category
  affiliation
  price
  currency
  quantity
}

/**
 * Process an analytics item with default values.
 *
 * @param {AnalyticsItem} item
 * @returns {AnalyticsItem}
 */
function getItem(item) {
  return {
    item_id: item.item_id,
    item_name: item.item_name,
    item_brand: item.item_brand,
    item_category: item.item_category,
    affiliation: item.affiliation,
    price: item.price || "0",
    currency: item.currency || "EUR",
    quantity: parseInt(item.quantity, 10) || 1,
  }
}

/**
 * Send a booking conversion event to various analytics platforms.
 *
 * Forward the information about the booked item and the user to Algolia and Google
 * Analytics.
 *
 * @example
 *   window.track.booking({
 *     item_id: "talk-123",
 *     item_name: "PyConDE & PyData Berlin 2020",
 *     item_brand: "PyConDE & PyData Berlin",
 *     item_category: "talk",
 *     price: 420.69,
 *     currency: "EUR",
 *     quantity: 42,
 *   })
 *
 * @param {object} params - The parameters of the booking.
 * @param {string} params.booking_id - The unique identifier of the booking.
 * @param {AnalyticsItem} params.args - The item that was booked.
 */
export function booking({ booking_id = "", ...args }) {
  const item = getItem(args)
  console.debug("Tracking event: booking", {
    booking_id,
    ...item,
  })
  let transaction_id = `booking-${item["item_id"]}-${globalThis.userId}`

  // For bookings that can have more than one booking per item_id
  if (booking_id) {
    transaction_id = `booking-${item["item_id"]}-${booking_id}-${globalThis.userId}`
  }

  gtag("event", "purchase", {
    transaction_id: transaction_id,
    value: item["price"],
    currency: item["currency"],
    items: [item],
  })

  globalThis.Brevo?.track(
    "purchase",
    { id: globalThis.userId },
    { value: item["price"] },
  )

  insightsClient("convertedObjectIDs", {
    authenticatedUserToken: globalThis.userId,
    index: globalThis.algoliaIndexName,
    eventName: `${item["item_category"]} Conversion`,
    objectIDs: [item["item_id"]],
  })
}

/**
 * Send a cancel booking event to various analytics platforms.
 *
 * @param {object} params - The parameters of the cancel booking event.
 * @param {string} params.booking_id - The unique identifier of the booking to cancel.
 * @param {AnalyticsItem} params.args - The item that was canceled.
 */
export function cancelBooking({ booking_id = "", ...args }) {
  const item = getItem(args)
  console.debug("Tracking event: refund", {
    booking_id,
    ...item,
  })

  let transaction_id = `booking-${item["item_id"]}-${globalThis.userId}`

  if (booking_id) {
    transaction_id = `booking-${item["item_id"]}-${booking_id}-${globalThis.userId}`
  }

  gtag("event", "refund", {
    transaction_id: transaction_id,
    value: item["price"],
    currency: item["currency"],
    items: [item],
  })
}

/**
 * Send a click item to various analytics platforms.
 *
 * Forward the information about the clicked item and the user to Algolia and Google
 * Analytics.
 *
 * @example
 *   window.track.click({
 *     item_id: "talk-123",
 *     item_name: "PyConDE & PyData Berlin 2020",
 *     item_brand: "PyConDE & PyData Berlin",
 *     affiliation: "PyConDE & PyData Berlin",
 *     price: 420.69,
 *     currency: "EUR",
 *     quantity: 42,
 *   })
 *
 * @param {object} params - The parameters of the click event.
 * @param {AnalyticsItem} params.args - The item that was clicked.
 */
export function click({ ...args }) {
  const item = getItem(args)
  console.debug("Tracking event: click", {
    ...item,
  })
  gtag("event", "select_item", {
    items: [item],
  })

  insightsClient("clickedObjectIDs", {
    authenticatedUserToken: globalThis.userId,
    index: globalThis.algoliaIndexName,
    eventName: "Item Selected",
    objectIDs: [item["item_id"]],
  })
}

/**
 * Send a view item to various analytics platforms. Forward the information about the
 * viewed item and the user to Google Analytics.
 *
 * @example
 *   window.track.viewItem({
 *     item_id: "talk-123",
 *     item_name: "PyConDE & PyData Berlin 2020",
 *     item_brand: "PyConDE & PyData Berlin",
 *     item_category: "talk",
 *     price: 420.69,
 *     currency: "EUR",
 *     quantity: 42,
 *   })
 *
 * @param {object} params - The parameters of the view event.
 * @param {AnalyticsItem} params.args - The item that was viewed.
 */
export function viewItem({ ...args }) {
  const item = getItem(args)
  console.debug("Tracking event: view_item", {
    ...item,
  })
  gtag("event", "view_item", {
    items: [item],
  })
}

/**
 * Send an add to wish list event to various analytics platforms.
 *
 * @param {object} params - The parameters of the click event.
 * @param {string} params.currency - The currency of the item (default: "EUR").
 * @param {number} params.value - The value of the item.
 * @param {AnalyticsItem} params.args - The item that was clicked.
 */
export function addToWishList({ currency = "EUR", value = 0, ...args }) {
  const item = getItem(args)
  console.debug("Tracking event: add_to_wishlist", {
    currency,
    value,
    ...item,
  })
  gtag("event", "add_to_wishlist", {
    currency,
    value,
    items: [item],
  })
}

/**
 * Send a remove from wish list event to various analytics platforms.
 *
 * @param {object} params - The parameters of the click event.
 * @param {string} params.currency - The currency of the item (default: "EUR").
 * @param {number} params.value - The value of the item.
 * @param {AnalyticsItem} params.args - The item that was clicked.
 */
export function removeFromWishList({ currency = "EUR", value = 0, ...args }) {
  const item = getItem(args)
  console.debug("Tracking event: remove_from_wishlist", {
    currency,
    value,
    ...item,
  })
  gtag("event", "remove_from_wishlist", {
    currency,
    value,
    items: [item],
  })
}

/**
 * Send a select promotion to various analytics platforms.
 *
 * Forward the information about the promotion and the user to Google Analytics.
 *
 * @example
 *   window.track.selectPromotion({
 *     promotion_name: "5 min break",
 *     promotion_id: "greeting-bar-pause",
 *     creative_name: "pause",
 *     creative_slot: 1,
 *   })
 *
 * @param {object} params - The parameters of the click event.
 * @param {string} params.promotion_name - The name of the promotion.
 * @param {string} params.promotion_id - The unique identifier of the promotion.
 * @param {string} params.creative_name - The creative name of promotion.
 * @param {string} params.creative_slot - The creative slot of the promotion.
 * @param {AnalyticsItem} params.args - The item that was selected.
 */
export function selectPromotion({
  promotion_name,
  promotion_id,
  creative_name,
  creative_slot,
  ...args
}) {
  const item = getItem(args)
  console.debug("Tracking event: select_promotion", {
    promotion_name,
    promotion_id,
    creative_name,
    creative_slot,
    ...item,
  })
  gtag("event", "select_promotion", {
    promotion_name,
    promotion_id,
    creative_name,
    creative_slot,
    items: [item],
  })
}

/**
 * Send a view promotion to various analytics platforms.
 *
 * Forward the information about the promotion and the user to Google Analytics.
 *
 * @example
 *   window.track.viewPromotion({
 *     promotion_name: "5 min break",
 *     promotion_id: "greeting-bar-pause",
 *     creative_name: "pause",
 *     creative_slot: 1,
 *   })
 *
 * @param {object} params - The parameters of the click event.
 * @param {string} params.promotion_name - The name of the promotion.
 * @param {string} params.promotion_id - The unique identifier of the promotion.
 * @param {string} params.creative_name - The creative name of promotion.
 * @param {string} params.creative_slot - The creative slot of the promotion.
 */
export function viewPromotion({
  promotion_name,
  promotion_id,
  creative_name,
  creative_slot,
  ...args
}) {
  const item = getItem(args)
  console.debug("Tracking event: view_promotion", {
    promotion_name,
    promotion_id,
    creative_name,
    creative_slot,
    ...item,
  })
  gtag("event", "view_promotion", {
    promotion_name,
    promotion_id,
    creative_name,
    creative_slot,
    items: [item],
  })
}

/**
 * Send a custom user event to various analytics platforms.
 *
 * @param {string} eventName - The name of the custom event.
 * @param {string} userId - The unique identifier of the user.
 */
export function userEvent(eventName, userId) {
  gtag("event", "custom_event", {
    event_name: eventName,
    user_id: userId,
  })
}

globalThis.track = globalThis.track || {
  booking,
  cancelBooking,
  click,
  viewItem,
  selectPromotion,
  viewPromotion,
  addToWishList,
  removeFromWishList,
  userEvent,
}

/**
 * Promotes the child element by tracking the container visibility and clicks for
 * analytics.
 *
 * This component sets up tracking for the child element, observing this component
 * visibility and clicks to send analytics events when the promotion is viewed or
 * selected.
 *
 * If any child element has a valid `href`, the component will append tracking
 * parameters to the URL.
 *
 * @class PromotionItem
 * @property {string} promotionId - The unique identifier for the promotion.
 * @property {string} promotionName - The name of the promotion.
 * @property {string} creativeName - The name of the creative associated with the
 *   promotion.
 * @property {string} creativeSlot - The slot of the creative in the promotion.
 * @property {string} itemId - The unique identifier for the item being promoted.
 * @property {string} itemName - The name of the item being promoted.
 * @property {string} itemBrand - The brand of the item being promoted.
 * @property {string} itemCategory - The category of the item being promoted.
 * @property {number} price - The price of the item being promoted (default: 0).
 * @property {string} currency - The currency of the price (default: 'EUR').
 * @property {number} quantity - The quantity of the item being promoted (default: 1).
 * @element promotion-item
 */
class PromotionItem extends HTMLElement {
  connectedCallback() {
    this.promotionEvent = {
      promotion_id: this.getAttribute("promotionId"),
      promotion_name: this.getAttribute("promotionName"),
      creative_name: this.getAttribute("creativeName"),
      creative_slot: this.getAttribute("creativeSlot"),
      item_id: this.getAttribute("itemId"),
      item_name: this.getAttribute("itemName"),
      item_brand: this.getAttribute("itemBrand"),
      item_category: this.getAttribute("itemCategory"),
      price: parseFloat(this.getAttribute("price") || "0"),
      currency: this.getAttribute("currency") || "EUR",
      quantity: parseInt(this.getAttribute("quantity") || "1", 10),
    }

    this.applyPromotionAttributes(this)
    this.setupIntersectionObserver(this)
    this.setupClickObserver(this)
  }

  disconnectedCallback() {
    this.intersectionObserver?.disconnect()
    this.removeEventListener("click", this.onSelect)
  }

  applyPromotionAttributes(targetElement) {
    targetElement.querySelectorAll("a").forEach((element) => {
      const href = element.getAttribute("href")
      element.setAttribute(
        "href",
        getUrlWithTracking(href, {
          promotion_id: this.promotionEvent.promotion_id,
          promotion_name: this.promotionEvent.promotion_name,
          creative_name: this.promotionEvent.creative_name,
          creative_slot: this.promotionEvent.creative_slot,
        }),
      )
      console.debug("Tracking URL:", element.getAttribute("href"))
    })
  }

  setupIntersectionObserver(targetElement) {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.5,
    }
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          globalThis.track.viewPromotion({ ...this.promotionEvent })
          this.intersectionObserver.unobserve(entry.target)
        }
      })
    }, options)
    this.intersectionObserver.observe(targetElement)
  }

  setupClickObserver(targetElement) {
    this.onSelect = this.onSelect.bind(this)
    targetElement.addEventListener("click", this.onSelect, false)
  }

  onSelect(event) {
    globalThis.track.selectPromotion({ ...this.promotionEvent })
  }
}

customElements.define("promotion-item", PromotionItem)

class BookingItem extends HTMLElement {
  connectedCallback() {
    this.bookingEvent = {
      booking_id: this.getAttribute("bookingId") || undefined,
      item_id: this.getAttribute("itemId"),
      item_name: this.getAttribute("itemName"),
      item_brand: this.getAttribute("itemBrand"),
      item_category: this.getAttribute("itemCategory"),
      price: parseFloat(this.getAttribute("price") || "0"),
      currency: this.getAttribute("currency") || "EUR",
      quantity: parseInt(this.getAttribute("quantity") || "1", 10),
      affiliation: this.getAttribute("affiliation") || undefined,
    }

    this.setupSubmitObserver(this)
  }

  disconnectedCallback() {
    this.removeEventListener("submit", this.onBooking)
  }

  setupSubmitObserver(targetElement) {
    this.onBooking = this.onBooking.bind(this)
    targetElement.addEventListener("submit", this.onBooking, false)
  }

  onBooking(event) {
    globalThis.track.booking({ ...this.bookingEvent })
  }
}

customElements.define("booking-item", BookingItem)

class CancelBooking extends HTMLElement {
  connectedCallback() {
    this.cancelBookingEvent = {
      booking_id: this.getAttribute("bookingId") || "",
      item_id: this.getAttribute("itemId"),
      item_name: this.getAttribute("itemName"),
      item_brand: this.getAttribute("itemBrand"),
      item_category: this.getAttribute("itemCategory"),
      price: parseFloat(this.getAttribute("price") || "0"),
      currency: this.getAttribute("currency") || "EUR",
      quantity: parseInt(this.getAttribute("quantity") || "1", 10),
      affiliation: this.getAttribute("affiliation") || "",
    }

    this.setupSubmitObserver(this)
  }

  disconnectedCallback() {
    this.removeEventListener("submit", this.onCancelBooking)
  }

  setupSubmitObserver(targetElement) {
    this.onCancelBooking = this.onCancelBooking.bind(this)
    targetElement.addEventListener("submit", this.onCancelBooking, false)
  }

  onCancelBooking(event) {
    globalThis.track.cancelBooking({ ...this.cancelBookingEvent })
  }
}

customElements.define("cancel-booking", CancelBooking)

class AddToWishList extends HTMLElement {
  connectedCallback() {
    this.wishListEvent = {
      item_id: this.getAttribute("itemId"),
      item_name: this.getAttribute("itemName"),
      item_brand: this.getAttribute("itemBrand"),
      item_category: this.getAttribute("itemCategory"),
      price: parseFloat(this.getAttribute("price") || "0"),
      currency: this.getAttribute("currency") || "EUR",
      quantity: parseInt(this.getAttribute("quantity") || "1", 10),
    }

    this.setupSubmitObserver(this)
  }

  disconnectedCallback() {
    this.removeEventListener("submit", this.onAddToWishList)
  }

  setupSubmitObserver(targetElement) {
    this.onAddToWishList = this.onAddToWishList.bind(this)
    targetElement.addEventListener("submit", this.onAddToWishList, false)
  }

  onAddToWishList(event) {
    globalThis.track.addToWishList({ ...this.wishListEvent })
  }
}

customElements.define("add-to-wishlist", AddToWishList)
